import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isPhoneLandscape } = useResponsive();
  const showHamburger = isMobile || isPhoneLandscape;

  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";

  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setIsOpen(false);
    clearAuthState();
    setIsAuthenticated(false);
    window.location.replace("/login");
  };

  const menuLinks = [
    { name: "Docs", path: "/docs" },
    { name: "Search", path: "/docs/search" },
  ];

  const navLinkClass = ({ isActive }) =>
    isActive
      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
      : "text-[120%] hover:opacity-90 focus:opacity-90";

  const navLinkStyle = { color: theme.accentColor || "#00838F" };

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#E0F7FA",
        color: theme.textColor || "#006064",
        position: "sticky",
        top: 0,
        zIndex: 1000,
        borderBottom: `1px solid ${theme.primaryColor}25`,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return isActive
                ? `font-bold ${sizeCls} hover:opacity-90 focus-visible:opacity-90`
                : `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: theme.accentColor || "#00838F" }}
          >
            X20 Publications
          </NavLink>
        </div>

        {/* Hamburger */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none"
            style={{ backgroundColor: theme.backgroundColor, color: theme.accentColor }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Desktop nav */}
        <nav className={showHamburger ? "hidden" : "flex space-x-4 items-center"}>
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={navLinkClass}
                  style={navLinkStyle}
                  end={link.path === "/"}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className={navLinkClass}
                style={navLinkStyle}
              >
                Logout
              </NavLink>
            </>
          ) : (
            !isAuthPage && (
              <NavLink
                to="/login"
                style={{
                  backgroundColor: theme.accentColor || "#00838F",
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "6px 20px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Sign in
              </NavLink>
            )
          )}
        </nav>
      </div>

      {/* Mobile dropdown */}
      {showHamburger && isOpen && (
        <div
          className="flex flex-col space-y-3 mt-3 pl-5"
          style={{ borderTop: `1px solid ${theme.primaryColor}20`, paddingTop: "12px" }}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={navLinkClass}
                  style={navLinkStyle}
                  onClick={() => setIsOpen(false)}
                  end={link.path === "/"}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className={navLinkClass}
                style={navLinkStyle}
              >
                Logout
              </NavLink>
            </>
          ) : (
            !isAuthPage && (
              <NavLink
                to="/login"
                onClick={() => setIsOpen(false)}
                style={{
                  backgroundColor: theme.accentColor,
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "8px 22px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  display: "inline-block",
                  width: "fit-content",
                }}
              >
                Sign in
              </NavLink>
            )
          )}
        </div>
      )}
    </header>
  );
};

export default withTheme(Header);
